import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import api from '../services/api';
import { 
  Calendar, 
  MapPin, 
  Clock, 
  Shield,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  X
} from 'lucide-react';

const GameRsvpAlertBanner = () => {
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissedGames, setDismissedGames] = useState(new Set());

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [alertsRes, dismissedRes] = await Promise.all([
          api.get('/tournaments/alerts/game-rsvps'),
          api.get('/notifications/dismissed/game_rsvp')
        ]);
        const gamesNeedingRsvp = (alertsRes.data.games || []).filter(g => g.needs_rsvp);
        setGames(gamesNeedingRsvp.slice(0, 5));
        setDismissedGames(new Set(dismissedRes.data || []));
      } catch (error) {
        console.error('Failed to fetch game RSVP alerts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleDismiss = async (gameId) => {
    setDismissedGames(prev => new Set([...prev, gameId]));
    try {
      await api.post(`/notifications/game_rsvp/${gameId}/dismiss`);
    } catch (error) {
      console.error('Failed to dismiss game RSVP:', error);
    }
  };

  const visibleGames = games.filter(g => !dismissedGames.has(g.game_id));

  if (loading || visibleGames.length === 0) {
    return null;
  }

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Card className="border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-white" data-testid="game-rsvp-alert-banner">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="p-2 bg-emerald-500 rounded-lg">
            <Calendar className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Upcoming Games - RSVP Required</h3>
            <p className="text-sm text-slate-500">Your child's team has upcoming games</p>
          </div>
        </div>
        
        <div className="space-y-3">
          {visibleGames.map(game => (
            <div 
              key={game.game_id}
              className="bg-white rounded-lg border border-slate-200 p-3"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900">vs {game.opponent_team}</span>
                    <Badge variant="outline" className="uppercase text-xs">
                      {game.age_category}
                    </Badge>
                    {game.team_name && (
                      <Badge className="bg-blue-100 text-blue-700 text-xs">
                        <Shield className="h-3 w-3 mr-1" />
                        {game.team_name}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(game.game_date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {game.start_time}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {game.location}
                    </span>
                  </div>
                  
                  {/* Children RSVP status */}
                  <div className="flex items-center gap-2 mt-2">
                    {game.eligible_children?.map(child => (
                      <span 
                        key={child.player_id}
                        className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${
                          child.has_rsvped 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {child.has_rsvped ? (
                          <CheckCircle className="h-3 w-3" />
                        ) : (
                          <AlertCircle className="h-3 w-3" />
                        )}
                        {child.player_name}: {child.has_rsvped ? child.rsvp_response : 'Pending'}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="flex flex-col gap-2 ml-3 flex-shrink-0">
                  <Button 
                    size="sm"
                    className="bg-emerald-500 hover:bg-emerald-600"
                    onClick={() => navigate('/parent/tournaments')}
                    data-testid={`rsvp-game-${game.game_id}`}
                  >
                    RSVP
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-slate-600 h-7 text-xs"
                    onClick={() => handleDismiss(game.game_id)}
                    data-testid={`dismiss-rsvp-${game.game_id}`}
                  >
                    <X className="h-3 w-3 mr-1" />
                    Dismiss
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {visibleGames.length > 0 && (
          <div className="mt-3 text-center">
            <Button 
              variant="link" 
              className="text-emerald-600"
              onClick={() => navigate('/parent/tournaments')}
            >
              View All Tournaments & Games
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GameRsvpAlertBanner;
